import { InvalidUUIDError, MissingQuoteError } from './ErrorComponents';

// classes and types
import { BusinessType } from '../../../Typescript';
import { Language } from '../../../Typescript/classes';
import type {
  AmendedFormFieldsFragment,
  FinalFormsByInternalnameQuery,
  QuoteQuery,
  SomeFinalFormsQuery,
} from '../../../gql/graphql';
import {
  QuoteWizardForm,
  QuoteWizardAnswerInstance,
  QuoteWizardQuestionInstance,
} from '../classes';
import { PolicySummary } from '../types';
import { BackendCompletedForm } from '../../../Typescript/backend/classes';
import { ProgramBuilderForm } from '../../FormManager/classes';

import type { QuoteStatus } from '../../../Typescript/backend/types';
import { UnifiedAnswerInstance } from '../../../Typescript/classes';

const findQuestionInstanceInList = (
  question: QuoteWizardQuestionInstance,
  list: Array<QuoteWizardQuestionInstance>,
): null | QuoteWizardQuestionInstance => {
  //console.log('Beginning search for ', question, 'in ', list);
  if (!list || list.length === 0) {
    //console.log('Empty list - exiting search');
    return null;
  }

  let foundQuestion: QuoteWizardQuestionInstance | null;
  for (let formQuestion of list) {
    // check only against the apiName so that minor changes to e.g. the question's label are accounted for
    if (question.apiName === formQuestion.apiName) {
      /*console.log('Found matching question for', question, formQuestion); */
      foundQuestion = formQuestion;
      return foundQuestion;
    } else {
      foundQuestion = findQuestionInstanceInList(
        question,
        formQuestion.subQuestions,
      );
      if (foundQuestion) {
        /*console.log( */
        /*   'Found matching question (as subquestion) for', */
        /*   question, */
        /*   formQuestion, */
        /* ); */
        return foundQuestion;
      }
    }
  }
  /*console.log('No matched question for', question); */
  return null;
};

export type ResumeQuoteQueryData = QuoteQuery;

type ResumeQuote = NonNullable<QuoteQuery['quote']>;

type ResumeCompletedForm = ResumeQuote['completedForms'][number];

export type SomeFinalFormsQueryData =
  | SomeFinalFormsQuery
  | FinalFormsByInternalnameQuery;

export const findMatchingBusinessInformationForm = <
  T extends { businessLine: { id: string } },
>(
  businessLine: Pick<BusinessType, 'id'>,
  formsList: Array<T>,
): T | undefined => {
  return formsList.find((form) => form.businessLine.id === businessLine.id);
};

/* export const findMatchingPolicyForm = ( */
/*   businessLine: BusinessType, */
/*   policy: $TSFixMe, */
/*   formsList: Array<$TSFixMe>, */
/* ) => { */
/*   return formsList.find( */
/*     (form) => */
/*       form.businessLine.id === businessLine.id && */
/*       form.policy && */
/*       form.policy.id === policy.id, */
/*   ); */
/* }; */

export interface ParsedAnswers {
  form: QuoteWizardForm;
  answers: Array<QuoteWizardAnswerInstance>;
}
interface ParseDataReturn {
  businessLine: BusinessType;
  selectedPolicies: Array<PolicySummary>;
  businessForm: QuoteWizardForm | undefined;
  policyForms: Array<QuoteWizardForm>;
  parsedAnswers: Array<ParsedAnswers>;
}
interface ParseInternalNameDataReturn {
  internalNameForms: Array<QuoteWizardForm>;
  parsedAnswers: Array<ParsedAnswers>;
}

export const getCompletedFormInternalNames = (
  quoteData: ResumeQuoteQueryData,
): Array<string> => {
  const quote = quoteData.quote;
  if (!quote) {
    return [];
  }

  const internalNames = quote.completedForms
    .filter((completedForm) => completedForm.internalName)
    .map((completedForm) => completedForm.internalName as string);
  return [...new Set(internalNames)];
};

const getAmendedFormsFromQueryData = (
  formsData: SomeFinalFormsQueryData,
): Array<AmendedFormFieldsFragment> => {
  return (formsData.someFinalForms ?? []).filter(
    (form): form is AmendedFormFieldsFragment => form != null,
  );
};

const convertFormsDataToQuoteWizardForms = (
  formsData: SomeFinalFormsQueryData,
  quote: ResumeQuote,
  quoteStatus: QuoteStatus,
): Array<QuoteWizardForm> => {
  if (!quote.language) {
    throw new Error('Quote is missing required language data');
  }

  const quoteLanguage = new Language(
    quote.language.shortName,
    quote.language.fullName,
  );

  return getAmendedFormsFromQueryData(formsData)
    .map((form) => ProgramBuilderForm.generateFromBackendResponse(form))
    .filter((form: ProgramBuilderForm) =>
      quoteLanguage.inLanguageList(form.getAvailableLanguages()),
    )
    .map((form: ProgramBuilderForm) => form.toQuoteWizardForm(quoteLanguage))
    .map((form: QuoteWizardForm) =>
      (quoteStatus === 'RENEWAL_IN_PROGRESS' ||
        quoteStatus === 'INCOMPLETE_RENEWAL') &&
      form.hasRenewalQuestions()
        ? form.filterQuestions(
            (question: QuoteWizardQuestionInstance) => question.askOnRenewal,
          )
        : form,
    );
};

const parseAnswersForCompletedForms = (
  completedForms: Array<BackendCompletedForm>,
  actualForms: Array<QuoteWizardForm>,
  quoteLanguage: Language,
  quoteStatus: QuoteStatus,
  findMatchingForm: (
    completedForm: BackendCompletedForm,
    actualForms: Array<QuoteWizardForm>,
  ) => QuoteWizardForm | undefined,
): Array<ParsedAnswers> => {
  const parsedAnswers: Array<ParsedAnswers> = [];

  completedForms.forEach((form) => {
    const matchingForm = findMatchingForm(form, actualForms);
    if (!matchingForm) {
      return;
    }

    const answersSoFar: Array<QuoteWizardAnswerInstance> = [];
    form.answers.forEach((answer) => {
      const mappedAnswer = mapAnswerToReduxState(
        answer,
        matchingForm.questionInstances,
        quoteLanguage,
        quoteStatus === 'RENEWAL_IN_PROGRESS' &&
          matchingForm.hasRenewalQuestions(),
      );
      if (mappedAnswer) {
        answersSoFar.push(mappedAnswer);
      }
    });
    parsedAnswers.push({
      form: matchingForm,
      answers: answersSoFar.filter((ele) => ele !== null),
    });
  });

  return parsedAnswers;
};
/**
 * Parses the quote data returned from the backend and packages it so we can translate it into the Redux store
 * quoteData - the original quote information (response from the backend)
 * formsData - the up-to-date version of all forms, &c. (response from the backend)
 * quoteStatus - the status of the quote - we need to change what we actually do based on whether it is renewing, &c.
 */
export const parseData = (
  quoteData: ResumeQuoteQueryData,
  formsData: SomeFinalFormsQueryData,
  quoteStatus: QuoteStatus,
): ParseDataReturn => {
  const quote = quoteData.quote;
  if (!quote?.businessLine || !quote.language) {
    throw new Error('Quote is missing required business line or language data');
  }

  const businessLine = quote.businessLine as BusinessType;

  // if the quote knows its selected policies, use those; otherwise harvest them from the forms
  const selectedPolicies: Array<Partial<PolicySummary>> = quote.completedForms
    .filter((completedForm) => completedForm.policy)
    .map((completedForm) => ({ ...completedForm.policy! }));

  let businessForm: QuoteWizardForm | undefined;
  const policyForms: Array<QuoteWizardForm> = [];
  const parsedAnswers: Array<ParsedAnswers> = [];

  const allQuoteForms = quote.completedForms.map((form: ResumeCompletedForm) =>
    BackendCompletedForm.generateFromBackendResponse(form),
  );
  const quoteLanguage = new Language(
    quote.language.shortName,
    quote.language.fullName,
  );
  const allActualForms = convertFormsDataToQuoteWizardForms(
    formsData,
    quote,
    quoteStatus,
  );

  // now go through the policy forms and use that to set the `required` and `qualifyingQuestion` fields on the parsed policies
  selectedPolicies.forEach((policy) => {
    const relatedForm = allActualForms.find(
      (form) => form.policy && form.policy.id === policy.id,
    );
    if (!relatedForm) {
      return;
    }
    policy.required = relatedForm.required;
    policy.qualifyingQuestion = relatedForm.qualifyingQuestion;
  });

  // get all policies that exist in a form and grab the business information and policy forms
  // (just split them into the businessForm and policyForms)
  allActualForms.forEach((form) => {
    if (form.internalName) {
      return;
    }
    form.businessLine = businessLine;
    if (form.policy) {
      // selectedPolicies.push(form.policy);
      policyForms.push(form);
    } else {
      businessForm = form;
    }
  });

  const businessPolicyQuoteForms = allQuoteForms.filter(
    (form) => !form.internalName,
  );
  parsedAnswers.push(
    ...parseAnswersForCompletedForms(
      businessPolicyQuoteForms,
      allActualForms,
      quoteLanguage,
      quoteStatus,
      (completedForm, actualForms) =>
        completedForm.findMatchByPolicy(actualForms),
    ),
  );

  return {
    businessLine,
    selectedPolicies: selectedPolicies as Array<PolicySummary>,
    businessForm,
    policyForms,
    parsedAnswers,
  };
};

export const parseInternalNameData = (
  quoteData: ResumeQuoteQueryData,
  formsData: SomeFinalFormsQueryData,
  quoteStatus: QuoteStatus,
): ParseInternalNameDataReturn => {
  const quote = quoteData.quote;
  if (!quote?.language) {
    throw new Error('Quote is missing required language data');
  }

  const allActualForms = convertFormsDataToQuoteWizardForms(
    formsData,
    quote,
    quoteStatus,
  );
  const quoteLanguage = new Language(
    quote.language.shortName,
    quote.language.fullName,
  );
  const internalNameQuoteForms = quote.completedForms
    .filter((form) => form.internalName)
    .map((form) => BackendCompletedForm.generateFromBackendResponse(form));

  const parsedAnswers = parseAnswersForCompletedForms(
    internalNameQuoteForms,
    allActualForms,
    quoteLanguage,
    quoteStatus,
    (completedForm, actualForms) =>
      actualForms.find(
        (actualForm) => actualForm.internalName === completedForm.internalName,
      ),
  );

  return {
    internalNameForms: allActualForms,
    parsedAnswers,
  };
};

export function mapAnswerToReduxState(
  answer: UnifiedAnswerInstance,
  questionList: Array<QuoteWizardQuestionInstance>,
  language: Language,
  stripRenewalAnswers: boolean = false,
): QuoteWizardAnswerInstance | null {
  const originalQuestion = answer.toQuoteWizardQuestionInstance(language);
  const matchedQuestionInstance = findQuestionInstanceInList(
    originalQuestion,
    questionList,
  );
  if (!matchedQuestionInstance || answer.value === 'N/A') {
    return null;
  }

  // don't prefill answers which shouldn't be on renewal
  if (stripRenewalAnswers && !matchedQuestionInstance.prefillOnRenewal) {
    return null;
  }
  /*console.log( */
  /*   'Constructing new QuoteWizardAnswerInstance from answer', */
  /*   answer, */
  /* ); */
  let parsedValue: unknown;
  if (answer.value === undefined) {
    parsedValue = undefined;
  } else if (answer.component === 'checkbox') {
    parsedValue =
      answer.value === 'true'
        ? true
        : answer.value === 'false'
        ? false
        : undefined;
  } else {
    parsedValue = answer.value;
  }
  const input = {
    questionInstance: matchedQuestionInstance,
    value: parsedValue,
    subAnswers:
      answer.subAnswers && answer.subAnswers.length > 0
        ? answer.subAnswers.reduce(
            (acc: Array<QuoteWizardAnswerInstance>, subAnswer) => {
              const mapped = mapAnswerToReduxState(
                subAnswer,
                matchedQuestionInstance.subQuestions,
                language,
              );
              if (mapped) {
                acc = [...acc, mapped];
              }
              return acc;
            },
            [],
          )
        : [],
  };
  return new QuoteWizardAnswerInstance(input);
}

/**
 * Component-specific error handling
 * @param error - the error to process
 * @return - Either return a valid react element or nothing (to have control pass back to the handler function)
 */
export const baseHandleSpecificErrors = (
  error: { message: string },
  uuid: string,
) => {
  if (error.message.match(/is not a valid uuid/i)) {
    return <InvalidUUIDError uuid={uuid} />;
  }
  if (error.message.match(/quote matching query does not exist/i)) {
    return <MissingQuoteError uuid={uuid} />;
  }
};
